import type { components } from './generated/schema';

export type ApiSuccessEnvelope<TData> = components['schemas']['SuccessEnvelope'] & {
  data: TData;
};

export type ApiErrorEnvelope = components['schemas']['ErrorEnvelope'];

export class ApiError extends Error {
  status?: number;
  code?: string;
  details?: unknown;
  response?: Response;

  constructor(message: string, options?: { status?: number; code?: string; details?: unknown; response?: Response }) {
    super(message);
    this.name = 'ApiError';
    this.status = options?.status;
    this.code = options?.code;
    this.details = options?.details;
    this.response = options?.response;
  }
}

type ClientResult = {
  data?: unknown;
  error?: unknown;
  response?: Response;
};

const toErrorEnvelope = (value: unknown): {
  error?: unknown;
  code?: string;
  details?: unknown;
} | undefined => {
  if (!value || typeof value !== 'object') {
    return undefined;
  }

  return value as {
    error?: unknown;
    code?: string;
    details?: unknown;
  };
};

export function unwrapSuccess<TData>(result: ClientResult): ApiSuccessEnvelope<TData> {
  const { data, error, response } = result;

  if (error) {
    const rawEnvelope = toErrorEnvelope(error) ?? toErrorEnvelope(data);
    const errorObject = rawEnvelope?.error && typeof rawEnvelope.error === 'object'
      ? rawEnvelope.error as { message?: string; code?: string; details?: unknown }
      : null;
    const message =
      typeof rawEnvelope?.error === 'string'
        ? rawEnvelope.error
        : errorObject?.message || (error instanceof Error ? error.message : 'Request failed');
    throw new ApiError(message, {
      status: response?.status,
      code: rawEnvelope?.code ?? errorObject?.code,
      details: rawEnvelope?.details ?? errorObject?.details,
      response,
    });
  }

  if (!data) {
    throw new ApiError('Empty response from server', { status: response?.status });
  }

  const envelope = data as ApiSuccessEnvelope<TData>;

  if (envelope.success !== true || envelope.data === undefined) {
    throw new ApiError('Unexpected response shape', { status: response?.status });
  }

  return envelope;
}

export const unwrapData = <TData>(result: ClientResult): TData => unwrapSuccess<TData>(result).data;
